import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const optionalChainingCode = `// Architectural Logic: Optional Chaining (?.)
// Short-circuits the evaluation if the reference is nullish (null or undefined)
const userResponse = {
    data: {
        profile: {
            name: 'Alice',
            // settings is missing
        }
    }
};

// Returns undefined instead of throwing TypeErrors
const theme = userResponse.data.profile.settings?.theme; 

// Works with array access and function calls too
const firstAdmin = apiResponse.users?.[0]?.name;
const result = remoteObject.optionalMethod?.();`;

const nullishCoalescingCode = `// Performance Risk: || vs ??
// Logical OR (||) treats 0, '', and false as "missing"
const userSettings = {
    notifications: false,
    retryCount: 0,
    headerText: ''
};

// Problematic fallbacks with ||
const retry = userSettings.retryCount || 5; // Result: 5 (Wrong, should be 0)

// Precision fallbacks with ?? (Nullish Coalescing)
// Only triggers for null or undefined
const actualRetry = userSettings.retryCount ?? 5; // Result: 0 (Correct)
const notify = userSettings.notifications ?? true; // Result: false (Correct)`;

const apiSafetyPatternCode = `// Production Pattern: API Safety Layer
function processUserMetadata(apiData) {
    return {
        // Safe deep access with sensible types
        id: apiData?.metadata?.id ?? 'INTERNAL_ID_GEN',
        lastLogin: apiData?.metadata?.timestamps?.[0] ?? Date.now(),
        isPremium: apiData?.subscription?.status === 'active' ?? false
    };
}`;

const JSOptionalChaining = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Defensive Engineering</div>
                <h1>Optional Chaining & Nullish Coalescing</h1>
                <p className="hero-lead">
                    Master the art of defensive programming. Learn how to handle deep object
                    graphs and missing data with the precision of ES2020 operators,
                    minimizing runtime errors in production environments.
                </p>
            </div>

            <div className="content-section">
                <h2>The Short-Circuiting Mechanics</h2>
                <p>
                    The optional chaining operator (`?.`) allows you to read a property
                    deep within a chain of connected objects without having to manually
                    verify that each reference in the chain is valid. It functions via
                    **short-circuiting**: if the part before `?.` is `null` or
                    `undefined`, the expression stops and returns `undefined`.
                </p>
                <CodeBlock code={optionalChainingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Precision vs. Falsiness: The `??` Advantage</h2>
                <p>
                    JavaScript developers historically used the logical OR (`||`) to
                    provide default values. However, `||` is flawed for configuration
                    objects because it treats `0`, `false`, and `""` as falsy. The
                    **Nullish Coalescing** operator (`??`) specifically targets only
                    `null` and `undefined`.
                </p>
                <CodeBlock code={nullishCoalescingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Defensive API Integration</h2>
                <p>
                    In production applications, API responses are often unpredictable or
                    deeply nested. Combining `?.` and `??` allows you to create a
                    robust "Safety Layer" that ensures your UI components never
                    receive `undefined` where they expect a concrete value.
                </p>
                <CodeBlock code={apiSafetyPatternCode} language="javascript" />
            </div>

            <ProTip><p>
                    Prior to ES2020, production logs were often flooded with
                    `TypeError: Cannot read property 'x' of undefined`. While `?.`
                    fixes this, be careful not to **over-use** it. Over-usage can
                    silently hide bugs (e.g., if a property *should* always be there
                    but is missing due to a logic error), making them harder to trace.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Short-Circuiting:** Use `?.` to stop execution early on missing nodes.</li>
                    <li>**Defaults:** Always prefer `??` for numeric or boolean configuration defaults.</li>
                    <li>**Function Calls:** Use `callback?.()` for optional event listeners.</li>
                    <li>**Bracket Notation:** Use `obj?.[key]` for safe dynamic property access.</li>
                    <li>**Async Data:** Wrap all deeply nested API accesses in optional chains.</li>
                    <li> **Caution:** Do not use `?.` on the left-hand side of an assignment (e.g., `user?.name = 'Bob'` is a syntax error).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSOptionalChaining;



